import { useEffect, useRef } from "react";
import { useSession } from "../../../hooks/useSession/useSession";
import { useOrganization } from "../../../hooks/useOrganization/useOrganization";
import { useUserContext } from "../../../providers/UserProvider";

type UserFlowPayload = {
    name?: string;
    email: string;
    mcid: string;
    signed_up_opti_id?: string;
};

/**
 * Initiates the UserFlow session. Since the created date is one of the fields, a component was created so that
 * we can leverage the UserProvider and pull that information out.
 *
 * The email is normalized to lowercase so the same person is only ever counted as a single Userflow identity,
 * regardless of the casing they signed in with.
 * @constructor
 */
export const UserFlow = () => {
    const { user } = useSession();
    const { profile, organizationId } = useUserContext();
    const { organization, error: organizationError } = useOrganization({ id: organizationId });

    // The organization loads via SWR after the profile, so waiting for that lookup to settle keeps us
    // from identifying with a blank mcid and immediately re-identifying with the real one. A failed
    // lookup still counts as settled — better to identify without an mcid than to never identify.
    const organizationSettled = !organizationId || !!organization || !!organizationError;

    // The effect reruns as the profile and organization resolve. The Userflow stub queues every init
    // call for the real SDK to replay once it loads, so track initialization to keep that to one call.
    const initialized = useRef(false);

    useEffect(() => {
        const userflow = window.userflow;
        if (!userflow || !profile || !user) {
            return;
        }

        const email = profile.email?.toLowerCase() ?? "";
        const mcid = organization?.crmId ?? "";
        const signed_up_opti_id = profile.created?.toISOString();

        const payload: UserFlowPayload = {
            name: user.name, // using the okta name instead of the modifiable value
            email,
            mcid
        };
        if (signed_up_opti_id) {
            payload["signed_up_opti_id"] = signed_up_opti_id;
        }

        if (!initialized.current) {
            userflow.init(process.env.REACT_APP_USERFLOW_TOKEN);
            initialized.current = true;
        }

        if (!organizationSettled) {
            return;
        }

        // A blank email would be a shared identity key that collapses every such user into one
        // Userflow identity, so hold off until we have a stable key to identify against.
        if (!email) {
            return;
        }

        userflow.identify(email, payload);

        // Userflow does not support blank group ids, so only group the user once the MCID is known.
        if (mcid) {
            userflow.group(mcid, {
                name: organization?.name ?? ""
            });
        }
    }, [user, profile, organizationSettled, organization?.crmId, organization?.name]);

    return <></>;
};

UserFlow.displayName = "UserFlow";
